import React, { useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { FormError } from '../ui/FormError'
import { Spinner } from '@/app/lib/loader/loader'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { GalleryHorizontal, Check } from 'lucide-react'
import { carouselService, StoreProductCarousel, CarouselDisplayType } from '../../services/carousel.service'
import { SortableProductItem } from './SortableProductItem'
import { useQuery } from '@tanstack/react-query'
import { CatalogSelect } from '../ui/CatalogSelect'
import { CatalogProductOption } from '@/app/services/product.service'

const carouselItemSchema = z.object({
  id: z.number().optional(),
  product_id: z.number(),
  product_name: z.string(),
  sort_order: z.number(),
  is_active: z.boolean(),
})

const carouselSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  title: z.string().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  display_type: z.enum([
    'MANUAL',
    'BEST_SELLERS',
    'NEWEST',
    'PROMOTIONS',
    'CATEGORY',
    'DEPARTMENT',
    'PRODUCT_TYPE',
  ]),
  items_limit: z.coerce.number().min(1, 'Debe ser mayor a 0'),
  is_active: z.boolean(),
  category_id: z.number().nullable().optional(),
  department_id: z.number().nullable().optional(),
  product_type_id: z.number().nullable().optional(),
  items: z.array(carouselItemSchema).optional(),
}).superRefine((data, ctx) => {
  if (data.display_type === 'CATEGORY' && !data.category_id) {
    ctx.addIssue({ code: 'custom', message: 'Seleccione una categoría', path: ['category_id'] })
  }
  if (data.display_type === 'DEPARTMENT' && !data.department_id) {
    ctx.addIssue({ code: 'custom', message: 'Seleccione un departamento', path: ['department_id'] })
  }
  if (data.display_type === 'PRODUCT_TYPE' && !data.product_type_id) {
    ctx.addIssue({ code: 'custom', message: 'Seleccione un tipo de producto', path: ['product_type_id'] })
  }
  if (data.display_type === 'MANUAL' && (!data.items || data.items.length === 0)) {
    ctx.addIssue({ code: 'custom', message: 'Debe agregar al menos un producto', path: ['items'] })
  }
})

type CarouselFormValues = z.infer<typeof carouselSchema>

interface CarouselModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  carouselId: number | null
  onSuccess: () => void
}

export function CarouselModal({ open, onOpenChange, carouselId, onSuccess }: CarouselModalProps) {
  const isEditing = !!carouselId

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CarouselFormValues>({
    resolver: zodResolver(carouselSchema),
    defaultValues: {
      name: '',
      title: '',
      subtitle: '',
      display_type: 'MANUAL',
      items_limit: 10,
      is_active: true,
      category_id: null,
      department_id: null,
      product_type_id: null,
      items: [],
    }
  })

  const { fields: items, append, remove, move } = useFieldArray({
    control,
    name: 'items',
  })

  const displayType = watch('display_type')
  const isActiveValue = watch('is_active')

  const { data: carousel, isLoading } = useQuery({
    queryKey: ['carousel', carouselId],
    queryFn: () => carouselService.getCarousel(carouselId!),
    enabled: !!carouselId && open,
  })

  useEffect(() => {
    if (carousel && open) {
      reset({
        name: carousel.name,
        title: carousel.title || '',
        subtitle: carousel.subtitle || '',
        display_type: carousel.display_type,
        items_limit: carousel.items_limit,
        is_active: carousel.is_active,
        category_id: carousel.category_id,
        department_id: carousel.department_id,
        product_type_id: carousel.product_type_id,
        items: (carousel.items || []).map(item => ({
          id: item.id,
          product_id: item.product_id,
          product_name: item.product?.name || `Producto #${item.product_id}`,
          sort_order: item.sort_order,
          is_active: item.is_active,
        }))
      })
    }
  }, [carousel, open, reset])

  // Clean unneeded fields when changing display_type
  useEffect(() => {
    if (displayType !== 'CATEGORY') setValue('category_id', null)
    if (displayType !== 'DEPARTMENT') setValue('department_id', null)
    if (displayType !== 'PRODUCT_TYPE') setValue('product_type_id', null)
    if (displayType !== 'MANUAL') setValue('items', [])
  }, [displayType, setValue])

  const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
    move(dragIndex, hoverIndex)
  }, [move])

  const addProductFromSearch = (product: CatalogProductOption | null) => {
    if (!product) return
    const currentItems = watch('items') || []
    if (currentItems.some(i => i.product_id === product.id)) {
      toast.error('El producto ya está en la lista')
      return
    }
    append({
      product_id: product.id,
      product_name: product.name,
      sort_order: currentItems.length + 1,
      is_active: true,
    })
  }

  const onSubmit = async (data: CarouselFormValues) => {
    try {
      const payload: Partial<StoreProductCarousel> = {
        name: data.name,
        title: data.title || null,
        subtitle: data.subtitle || null,
        display_type: data.display_type,
        items_limit: data.items_limit,
        is_active: data.is_active,
        category_id: data.display_type === 'CATEGORY' ? data.category_id : null,
        department_id: data.display_type === 'DEPARTMENT' ? data.department_id : null,
        product_type_id: data.display_type === 'PRODUCT_TYPE' ? data.product_type_id : null,
      }

      if (data.display_type === 'MANUAL') {
        payload.items = (data.items || []).map((item, index) => ({
          id: item.id,
          product_id: item.product_id,
          sort_order: index + 1,
          is_active: item.is_active,
        }))
      }

      if (isEditing && carouselId) {
        await carouselService.updateCarousel(carouselId, payload)
        toast.success('Carrusel actualizado exitosamente')
      } else {
        await carouselService.createCarousel(payload)
        toast.success('Carrusel creado exitosamente')
      }
      onSuccess()
    } catch (error) {
      toast.error('Ocurrió un error al guardar el carrusel')
      console.error(error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-card-foreground">
              <GalleryHorizontal className="h-5 w-5 text-primary" />
              {isEditing ? 'Editar Carrusel' : 'Nuevo Carrusel'}
            </DialogTitle>
            <DialogDescription className="text-card-description">
              {isEditing
                ? 'Modifica los datos del carrusel de productos.'
                : 'Configura un nuevo carrusel para la tienda en línea.'}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <Input placeholder="Ej. Recomendados" {...register('name')} />
                  <FormError message={errors.name?.message} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Tipo de Carrusel <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={displayType}
                    onValueChange={(value) => setValue('display_type', value as CarouselDisplayType)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MANUAL">Manual (Elegir productos)</SelectItem>
                      <SelectItem value="BEST_SELLERS">Más vendidos</SelectItem>
                      <SelectItem value="NEWEST">Nuevos (Recientes)</SelectItem>
                      <SelectItem value="PROMOTIONS">Promociones</SelectItem>
                      <SelectItem value="CATEGORY">Por Categoría</SelectItem>
                      <SelectItem value="DEPARTMENT">Por Departamento</SelectItem>
                      <SelectItem value="PRODUCT_TYPE">Por Tipo de Producto</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormError message={errors.display_type?.message} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Título (Opcional)
                  </label>
                  <Input placeholder="Ej. Los más buscados" {...register('title')} />
                  <FormError message={errors.title?.message} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Subtítulo (Opcional)
                  </label>
                  <Input placeholder="Ej. Explora estos increíbles productos" {...register('subtitle')} />
                  <FormError message={errors.subtitle?.message} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Límite de ítems a mostrar <span className="text-red-500">*</span>
                  </label>
                  <Input type="number" {...register('items_limit')} />
                  <FormError message={errors.items_limit?.message} />
                </div>
                
                <div className="flex items-center gap-2 pt-8">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isActiveValue}
                    onClick={() => setValue('is_active', !isActiveValue)}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-primary' : 'bg-slate-200'
                      }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                        }`}
                    />
                  </button>
                  <span className="text-sm font-medium text-card-foreground">Carrusel Activo</span>
                </div>
              </div>

              {displayType === 'CATEGORY' && (
                <div className="space-y-2 pt-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Categoría <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="category_id"
                    control={control}
                    render={({ field }) => (
                      <CatalogSelect
                        catalog="ecommerce/catalog/options/categories"
                        extraParams={{ published: 1 }}
                        placeholder="Seleccionar categoría"
                        value={field.value ? String(field.value) : ''}
                        onChange={(val: any) => field.onChange(val ? Number(val) : null)}
                      />
                    )}
                  />
                  <FormError message={errors.category_id?.message} />
                </div>
              )}

              {displayType === 'DEPARTMENT' && (
                <div className="space-y-2 pt-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Departamento <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="department_id"
                    control={control}
                    render={({ field }) => (
                      <CatalogSelect
                        catalog="ecommerce/catalog/options/departments"
                        extraParams={{ published: 1 }}
                        placeholder="Seleccionar departamento"
                        value={field.value ? String(field.value) : ''}
                        onChange={(val: any) => field.onChange(val ? Number(val) : null)}
                      />
                    )}
                  />
                  <FormError message={errors.department_id?.message} />
                </div>
              )}

              {displayType === 'PRODUCT_TYPE' && (
                <div className="space-y-2 pt-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Tipo de Producto <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="product_type_id"
                    control={control}
                    render={({ field }) => (
                      <CatalogSelect
                        catalog="ecommerce/catalog/options/product-types"
                        extraParams={{ published: 1 }}
                        placeholder="Seleccionar tipo de producto"
                        value={field.value ? String(field.value) : ''}
                        onChange={(val: any) => field.onChange(val ? Number(val) : null)}
                      />
                    )}
                  />
                  <FormError message={errors.product_type_id?.message} />
                </div>
              )}

              {displayType === 'MANUAL' && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-card-foreground">
                      Agregar Producto Manualmente
                    </label>
                    <CatalogSelect
                      catalog="ecommerce/catalog/products"
                      extraParams={{ published: 1 }}
                      placeholder="Buscar y agregar producto..."
                      returnObject={true}
                      onChange={(item) => addProductFromSearch(item as CatalogProductOption | null)}
                    />
                    <FormError message={errors.items?.root?.message} />
                  </div>

                  <DndProvider backend={HTML5Backend}>
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                      {items.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4 border rounded-md">
                          No has agregado productos. Busca y selecciona uno para agregarlo al carrusel.
                        </p>
                      ) : (
                        items.map((field, index) => {
                          const liveItem = watch(`items.${index}`)
                          return (
                            <SortableProductItem
                              key={field.id}
                              index={index}
                              product={{ id: liveItem.product_id, name: liveItem.product_name }}
                              moveItem={moveItem}
                              removeItem={() => remove(index)}
                            />
                          )
                        })
                      )}
                    </div>
                  </DndProvider>
                </div>
              )}

            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-end mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
              {isSubmitting ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
